const OVERFLOW_TITLE_ATTR = "data-overflow-title-applied";
const IGNORE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "OPTION", "SVG", "PATH"]);

export function isHorizontallyOverflowing(element) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  const text = element.textContent?.replace(/\s+/g, " ").trim();
  if (!text) {
    return false;
  }
  return element.scrollWidth > element.clientWidth + 1;
}

export function shouldSkipOverflowTooltip(element) {
  if (!(element instanceof HTMLElement)) {
    return true;
  }
  if (IGNORE_TAGS.has(element.tagName)) {
    return true;
  }
  if (element.isContentEditable) {
    return true;
  }
  if (element.closest("[data-no-overflow-tooltip]")) {
    return true;
  }
  return false;
}

export function getElementTooltipText(element) {
  if (!(element instanceof HTMLElement)) {
    return "";
  }
  return (
    element.getAttribute("data-tooltip") ||
    element.getAttribute("aria-label") ||
    element.textContent?.replace(/\s+/g, " ").trim() ||
    ""
  );
}

export function resolveOverflowTooltipTarget(start) {
  if (!(start instanceof HTMLElement) || shouldSkipOverflowTooltip(start)) {
    return null;
  }

  const candidates = [];
  let node = start;
  while (node && node !== document.documentElement) {
    if (!shouldSkipOverflowTooltip(node)) {
      candidates.push(node);
    }
    if (node.tagName === "TD" || node.tagName === "TH" || node.tagName === "TABLE") {
      break;
    }
    node = node.parentElement;
  }

  for (const candidate of candidates) {
    if (isHorizontallyOverflowing(candidate)) {
      return candidate;
    }
  }

  const tableCell = start.closest("td, th");
  if (tableCell && !shouldSkipOverflowTooltip(tableCell)) {
    for (const child of tableCell.querySelectorAll("*")) {
      if (!shouldSkipOverflowTooltip(child) && isHorizontallyOverflowing(child)) {
        return child;
      }
    }
    if (isHorizontallyOverflowing(tableCell)) {
      return tableCell;
    }
  }

  for (const candidate of candidates) {
    for (const child of candidate.querySelectorAll("span, a, p, div, button, label")) {
      if (!shouldSkipOverflowTooltip(child) && isHorizontallyOverflowing(child)) {
        return child;
      }
    }
  }

  return null;
}

export function applyOverflowTooltip(element) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const text = getElementTooltipText(element);
  if (!text) {
    return false;
  }

  if (element.hasAttribute("title") && !element.hasAttribute(OVERFLOW_TITLE_ATTR)) {
    return false;
  }

  element.title = text;
  element.setAttribute(OVERFLOW_TITLE_ATTR, "true");
  return true;
}

export function clearOverflowTooltip(element) {
  if (!(element instanceof HTMLElement)) {
    return;
  }
  if (!element.hasAttribute(OVERFLOW_TITLE_ATTR)) {
    return;
  }
  element.removeAttribute("title");
  element.removeAttribute(OVERFLOW_TITLE_ATTR);
}

export function installOverflowTooltipListeners(root = document) {
  const handlePointerOver = (event) => {
    const target = resolveOverflowTooltipTarget(event.target);
    if (target) {
      applyOverflowTooltip(target);
    }
  };

  root.addEventListener("pointerover", handlePointerOver, true);

  return () => {
    root.removeEventListener("pointerover", handlePointerOver, true);
  };
}
